import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { supabase } from "../lib/supabase";
import { config } from "../lib/config";

// ✅ FIX 5: Define proper types instead of 'any'
interface TestResult {
  name: string;
  status: 'success' | 'error';
  details: Record<string, unknown>;
}

export function Debug() {
  const [tests, setTests] = useState<TestResult[]>([]); // ✅ FIX 5: Replace any[] with TestResult[]
  const [running, setRunning] = useState(false);

  const runTests = async () => {
    setRunning(true);
    const results: TestResult[] = []; // ✅ FIX 5: Replace any[] with TestResult[]

    // Test 1: Config Check
    results.push({
      name: "Configuration",
      status: config.supabase.url && config.supabase.anonKey ? "success" : "error",
      details: {
        url: config.supabase.url,
        hasKey: !!config.supabase.anonKey,
        keyPrefix: config.supabase.anonKey?.substring(0, 20) + "...",
      },
    });

    // Test 2: Supabase Connection
    try {
      const { data, error } = await supabase.from("products").select("count").limit(1);
      results.push({
        name: "Supabase Connection",
        status: error ? "error" : "success",
        details: { error: error?.message },
      });
    } catch (e) {
      // ✅ FIX 4: Proper error handling
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      results.push({
        name: "Supabase Connection",
        status: "error",
        details: { error: errorMessage },
      });
    }

    // Test 3: Products Table
    try {
      const { data, error } = await supabase.from("products").select("*").limit(5);
      const count = data?.length || 0;
      results.push({
        name: "Products Table",
        status: error ? "error" : "success",
        details: { count, error: error?.message },
      });
    } catch (e) {
      // ✅ FIX 4: Proper error handling
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      results.push({
        name: "Products Table",
        status: "error",
        details: { error: errorMessage },
      });
    }

    setTests(results);
    setRunning(false);
  };

  useEffect(() => {
    runTests();
  }, []);

  const getStatusIcon = (status: string) => {
    if (status === "success") return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (status === "error") return <XCircle className="w-5 h-5 text-red-500" />;
    return <AlertCircle className="w-5 h-5 text-yellow-500" />;
  };

  const getStatusBadge = (status: string) => {
    if (status === "success") return <Badge className="bg-green-500">Pass</Badge>;
    if (status === "error") return <Badge variant="destructive">Fail</Badge>;
    return <Badge variant="secondary">Warning</Badge>;
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl mb-3">Database Diagnostics</h1>
          <p className="text-xl text-muted-foreground">
            Check Supabase connection and database status
          </p>
        </div>

        <div className="mb-6 flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">
              {tests.length} tests completed
            </p>
          </div>
          <Button onClick={runTests} disabled={running}>
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Re-run Tests
              </>
            )}
          </Button>
        </div>

        <div className="space-y-4">
          {tests.map((test, index) => (
            <Card key={index} className="p-6 glass-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(test.status)}
                  <div>
                    <h3 className="font-medium">{test.name}</h3>
                  </div>
                </div>
                {getStatusBadge(test.status)}
              </div>

              <div className="p-4 rounded-lg bg-muted/30 font-mono text-xs overflow-x-auto">
                <pre>{JSON.stringify(test.details, null, 2)}</pre>
              </div>
            </Card>
          ))}
        </div>

        {tests.length > 0 && (
          <Card className="p-6 glass-card mt-6">
            <h3 className="font-medium mb-4">Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-green-500/10">
                <p className="text-2xl font-bold text-green-500">
                  {tests.filter((t) => t.status === "success").length}
                </p>
                <p className="text-sm text-muted-foreground">Passed</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-500/10">
                <p className="text-2xl font-bold text-red-500">
                  {tests.filter((t) => t.status === "error").length}
                </p>
                <p className="text-sm text-muted-foreground">Failed</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-yellow-500/10">
                <p className="text-2xl font-bold text-yellow-500">
                  {tests.filter((t) => t.status === "warning").length}
                </p>
                <p className="text-sm text-muted-foreground">Warnings</p>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 glass-card mt-6">
          <h3 className="font-medium mb-4">Next Steps</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>If any tests failed:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Check your Supabase project is active</li>
              <li>Verify environment variables are correct</li>
              <li>Run database/schema.sql in Supabase SQL Editor</li>
              <li>Run database/policies.sql in Supabase SQL Editor</li>
              <li>Add products to the products table</li>
            </ol>
            <p className="mt-4">
              See <code className="bg-muted px-1 py-0.5 rounded">TROUBLESHOOTING.md</code> for
              detailed help.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}